import { NgModule } from '@angular/core';
import {
  APOLLO_NAMED_OPTIONS,
  APOLLO_OPTIONS,
  ApolloModule,
  NamedOptions,
} from 'apollo-angular';
import { InMemoryCache, ApolloLink, split } from '@apollo/client/core';
import { WebSocketLink } from '@apollo/client/link/ws';
import { HttpLink } from 'apollo-angular/http';
import { HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environment';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { KeycloakService } from 'keycloak-angular';
import { onError } from '@apollo/client/link/error';
import { LoadingService } from './services/loading/loading.service';
import { BehaviorSubject } from 'rxjs';
import { RetryLink } from 'apollo-link-retry';
import { debounceTime, filter } from 'rxjs/operators';
import { ErrorPolicy } from 'apollo-client';

const secure_key = environment.subscriptionSecureKey;
const networkStatus = new BehaviorSubject(false);
let message = null;
// Use this url for test in local
// const ws_uri = "ws://localhost:8090/graphql/subscription";

// TODO REMOVE: temporary filter to ignore CycleType enum empty value error on prospectus save
const CYCLE_TYPE_EMPTY_ERROR =
  "Enum 'CycleType' cannot represent value: ''";
const CYCLE_TYPE_ERROR_PATH = 'updateProspectus.farmCategory.production.cycle';

function isCycleTypeEmptyError(err: { message?: string; path?: readonly (string | number)[] }): boolean {
  const pathStr = err.path?.join?.('.');
  return !!(
    err.message?.includes(CYCLE_TYPE_EMPTY_ERROR) &&
    pathStr === CYCLE_TYPE_ERROR_PATH
  );
}

function filterCycleTypeErrorLink(): ApolloLink {
  return new ApolloLink((operation, forward) =>
    forward(operation).map((response) => {
      if (!response.errors?.length) return response;
      const filtered = response.errors.filter((e) => !isCycleTypeEmptyError(e));
      if (filtered.length === 0) {
        const { errors, ...rest } = response;
        return rest;
      }
      return { ...response, errors: filtered };
    })
  );
}

export function createApollo(
  uri: string,
  httpLink: HttpLink,
  options?: { filterCycleTypeError?: boolean; authOnlyRetry?: boolean },
) {
  const ws_uri =
    (environment.protocol === 'https' ? 'wss' : 'ws') +
    uri.slice(uri.indexOf(':'));
  const keycloakService = new KeycloakService();
  const basic = setContext((operation, context) => ({
    headers: {
      Accept: 'charset=utf-8',
    },
  }));
  const auth = setContext(async (operation, context) => {
    const token = localStorage.getItem('access_token');
    if (token === null) {
      return {};
    } else {
      return {
        headers: new HttpHeaders({
          access_token: token,
        }),
      };
    }
  });

  const retryLink = new RetryLink();

  const http = ApolloLink.from([basic, auth, httpLink.create({ uri })]);

  const ws = new WebSocketLink({
    uri: ws_uri,
    options: {
      lazy: true,
      reconnect: true,
      connectionParams: async () => {
        const token = localStorage.getItem('access_token');
        return { access_token: token };
      },
    },
  });
  const link = split(
    ({ query }) => {
      let definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    ws,
    http,
  );
  const cache = new InMemoryCache({ addTypename: false });
  const defaultOptions = {
    watchQuery: {
      errorPolicy: 'all' as ErrorPolicy,
    },
  };

  // EPWA-994: con `authOnlyRetry` il retry scatta SOLO per errori di autenticazione,
  // dove rigiocare l'operazione con un token fresco può cambiare l'esito. Un errore
  // applicativo (es. `DataQualityError: No data found` degli agro-models) risponderebbe
  // identico: ritentarlo raddoppiava ogni query della Home Calculations.
  const isAuthError = (err: any): boolean => {
    const code = err?.extensions?.code;
    if (code === 'UNAUTHENTICATED' || code === 'FORBIDDEN') return true;
    const status = err?.extensions?.exception?.status ?? err?.extensions?.response?.status;
    return status === 401 || status === 403;
  };

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        // Un solo retry per operazione: senza questa guardia un errore persistente
        // rimanderebbe l'operazione all'infinito.
        const alreadyRetried = operation.getContext()['authRetry'] === true;
        const shouldRetry = options?.authOnlyRetry
          ? graphQLErrors.some(isAuthError) && !alreadyRetried
          : true;

        if (shouldRetry) {
          const oldHeaders = operation.getContext()['headers'];
          operation.setContext({
            authRetry: true,
            headers: {
              ...oldHeaders,
              authorization: localStorage.getItem('access_token'),
            },
          });
          // retry the request, returning the new observable
          return forward(operation);
        }
      }
      if (networkError) {
        console.log(`[Network error]:`, networkError);
        networkStatus.next(true);
        message = networkError;
        //return forward(operation);

        // if you would also like to retry automatically on
        // network errors, we recommend that you use
        // apollo-link-retry
      }
    },
  );

  const links: ApolloLink[] = [errorLink, link];
  if (options?.filterCycleTypeError) {
    links.unshift(filterCycleTypeErrorLink());
  }
  const httpLinkWithErrorHandling = ApolloLink.from(links);

  return {
    link: httpLinkWithErrorHandling,
    cache,
    defaultOptions,
    retryLink,
  };
}

@NgModule({
  imports: [ApolloModule],
  providers: [
    {
      provide: KeycloakService,
    },
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory(httpLink: HttpLink): NamedOptions {
        return {
          default: createApollo(environment.graphql, httpLink),
          // TODO REMOVE: filterCycleTypeError - remove when backend fixes CycleType empty value
          prospectus: createApollo(environment.prospectusServer, httpLink, { filterCycleTypeError: true }),
          // EPWA-994: gli agro-models rispondono `DataQualityError: No data found` quando
          // il sensore non ha dati nel periodo. È un esito legittimo, non un errore di auth:
          // senza authOnlyRetry ogni query della Home veniva rigiocata a vuoto.
          agromodels: createApollo(environment.agromodelsServer, httpLink, { authOnlyRetry: true }),
          co2: createApollo(environment.co2, httpLink),
        };
      },
      deps: [HttpLink],
    },
  ],
})
export class GraphQLModule {
  constructor(loadingService: LoadingService) {
    networkStatus
      .pipe(
        filter((v) => v),
        debounceTime(1000),
      )
      .subscribe(() =>
        loadingService.addFatalError({ type: 'NETWORK', message }),
      );
  }
}
